import { Alert, Box, Button, Grid, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { InvoiceGroup } from 'src/shared/models/invoiceGroup/invoiceGroup';
import { deleteEInvoiceSender, getEInvoiceSender } from 'src/shared/services/electronicInvoiceSenderService';
import { updateInvoiceGroup } from 'src/shared/services/invoiceGroupService';
import { EInvoiceConnectEditModal } from './eInvoiceConnectEditModal';
import { EInvoiceConnectModal } from './eInvoiceConnectModal';
import { KivraConnectModal } from './kivraConnectModal';
import { StripeConnectModal } from './stripeConnectModal';
import { KivraConnectEditModal } from './kivraConnectEditModal';

type PaymentMethodsConfigurationProps = {
  invoiceGroup: InvoiceGroup;
  refetchInvoiceGroup: () => void;
  eInvoiceSenderExists: boolean;
  onEInvoiceSenderStatusChange: (exists: boolean) => void;
};

export const PaymentMethodsConfiguration = (props: PaymentMethodsConfigurationProps) => {
  const { invoiceGroup, refetchInvoiceGroup, eInvoiceSenderExists, onEInvoiceSenderStatusChange } = props;
  const { t } = useTranslation();

  const [kivraConnectModal, setKivraConnectModal] = useState<boolean>(false);
  const [kivraConnectEditModal, setKivraConnectEditModal] = useState<boolean>(false);
  const [stripeConnectModal, setStripeConnectModal] = useState<boolean>(false);
  const [eInvoiceConnectModal, setEInvoiceConnectModal] = useState<boolean>(false);
  const [eInvoiceConnectEditModal, setEInvoiceConnectEditModal] = useState<boolean>(false);

  const refreshSenderStatus = () => {
    getEInvoiceSender(invoiceGroup.owner_id)
      .then((res) => onEInvoiceSenderStatusChange(!!res.data))
      .catch(() => onEInvoiceSenderStatusChange(false));
  };

  return (
    <>
      {/* Bankuppgifter */}
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12}>
          <Typography variant='h6' mb={-1} sx={{ fontWeight: 'bold' }}>
            {t('invoice:adminInvoiceGroupDetailPage.bankDetails')}
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <ControlledTextField label={t('invoice:adminInvoiceGroupDetailPage.accountType')} name='accountName' disabled />
        </Grid>
        <Grid item xs={12} md={6}>
          <ControlledTextField label={t('invoice:adminInvoiceGroupDetailPage.accountNumberLabel')} name='accountNr' disabled />
        </Grid>
        <Grid item xs={12} lg={7.5}>
          <ControlledTextField label={t('invoice:adminInvoiceGroupDetailPage.IBAN')} name='iban' disabled />
        </Grid>
        <Grid item xs={12} lg={4.5}>
          <ControlledTextField label={t('invoice:adminInvoiceGroupDetailPage.bic')} name='bic' disabled />
        </Grid>
      </Grid>

      {/* Autogiro */}
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12}>
          <Typography variant='h6' mb={-1} sx={{ fontWeight: 'bold' }}>
            {t('invoice:adminInvoiceGroupDetailPage.autogiroTitle')}
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <ControlledTextField label={t('invoice:adminInvoiceGroupDetailPage.customerNrBgc')} name='autogiroCustomerNr' disabled />
        </Grid>
        <Grid item xs={12} md={6}>
          <ControlledTextField label={t('invoice:adminInvoiceGroupDetailPage.autogiroAccount')} name='autogiroAccount' disabled />
        </Grid>
      </Grid>

      {/* Swish */}
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12}>
          <Typography variant='h6' mb={-1} sx={{ fontWeight: 'bold' }}>
            {t('invoice:adminInvoiceGroupDetailPage.swish')}
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <ControlledTextField label={t('invoice:adminInvoiceGroupDetailPage.swishNumberLabel')} name='swishAccount' disabled />
        </Grid>
        <Grid item xs={12} md={6}>
          <ControlledTextField label={t('invoice:adminInvoiceGroupDetailPage.swishRecurringNumberLabel')} name='swishRecurringAccount' disabled />
        </Grid>
      </Grid>

      {/* Stripe */}
      <Grid container mb={2}>
        <Grid item xs={12}>
          <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
            {t('invoice:adminInvoiceGroupDetailPage.stripeAccount')}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Alert
            icon={false}
            severity={!invoiceGroup.stripeAccount ? 'info' : 'success'}
            sx={{
              '& .MuiAlert-message': { width: '100% !important' },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              {!invoiceGroup.stripeAccount
                ? t('invoice:adminInvoiceGroupDetailPage.notConfigured')
                : t('invoice:adminInvoiceGroupDetailPage.connected')}

              {!invoiceGroup.stripeAccount && (
                <Button variant='outlined' size='small' disableElevation sx={{ px: 2 }} onClick={() => setStripeConnectModal(true)}>
                  {t('invoice:adminInvoiceGroupDetailPage.connectStripe')}
                </Button>
              )}

              {invoiceGroup.stripeAccount && (
                <Button variant='outlined' size='small' disableElevation sx={{ px: 2 }} onClick={() => setStripeConnectModal(true)}>
                  {t('common:button.edit')}
                </Button>
              )}
            </Box>
          </Alert>
        </Grid>
      </Grid>

      {/* Kivra */}
      <Grid container mb={2}>
        <Grid item xs={12}>
          <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
            {t('invoice:adminInvoiceGroupDetailPage.kivra')}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Alert
            icon={false}
            severity={invoiceGroup.kivraTenantKey && invoiceGroup.kivraTenantKey.length > 0 ? 'success' : 'info'}
            sx={{
              '& .MuiAlert-message': { width: '100% !important' },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              {invoiceGroup.kivraTenantKey && invoiceGroup.kivraTenantKey.length > 0
                ? t('invoice:adminInvoiceGroupDetailPage.connected')
                : t('invoice:adminInvoiceGroupDetailPage.notConfigured')}

              {invoiceGroup.kivraTenantKey && invoiceGroup.kivraTenantKey.length > 0 ? (
                <Button
                  variant='outlined'
                  size='small'
                  disableElevation
                  sx={{ px: 2 }}
                  onClick={() => {
                    setKivraConnectEditModal(true);
                    ConsoleHelper.log();
                  }}
                >
                  {t('common:button.edit')}
                </Button>
              ) : (
                <Button variant='outlined' size='small' disableElevation sx={{ px: 2 }} onClick={() => setKivraConnectModal(true)}>
                  {t('invoice:adminInvoiceGroupDetailPage.connectKivra')}
                </Button>
              )}
            </Box>
          </Alert>
        </Grid>
      </Grid>
      {/* E-faktura */}
      <Grid container mb={2}>
        <Grid item xs={12}>
          <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
            {t('invoice:adminInvoiceGroupDetailPage.eInvoice')}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Alert
            icon={false}
            severity={eInvoiceSenderExists ? 'success' : 'info'}
            sx={{
              '& .MuiAlert-message': { width: '100% !important' },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              {eInvoiceSenderExists ? t('invoice:adminInvoiceGroupDetailPage.connected') : t('invoice:adminInvoiceGroupDetailPage.notConfigured')}

              {!eInvoiceSenderExists && (
                <Button variant='outlined' size='small' disableElevation sx={{ px: 2 }} onClick={() => setEInvoiceConnectModal(true)}>
                  {t('invoice:adminInvoiceGroupDetailPage.connectEInvoice')}
                </Button>
              )}

              {eInvoiceSenderExists && (
                <Button variant='outlined' size='small' disableElevation sx={{ px: 2 }} onClick={() => setEInvoiceConnectEditModal(true)}>
                  {t('common:button.edit')}
                </Button>
              )}
            </Box>
          </Alert>
        </Grid>
      </Grid>

      {eInvoiceConnectEditModal && (
        <EInvoiceConnectEditModal
          open={eInvoiceConnectEditModal}
          onClose={() => setEInvoiceConnectEditModal(false)}
          invoiceGroupId={invoiceGroup.owner_id}
          emailNotification={invoiceGroup.electronicInvoiceEmailNotification || ''}
          onSaveEmailNotification={async (email) => {
            await updateInvoiceGroup({ ...invoiceGroup, electronicInvoiceEmailNotification: email });
            refetchInvoiceGroup();
          }}
          onDisconnect={async () => {
            await deleteEInvoiceSender(invoiceGroup.owner_id);
            refreshSenderStatus();
            refetchInvoiceGroup();
          }}
        />
      )}
      {eInvoiceConnectModal && (
        <EInvoiceConnectModal
          open={eInvoiceConnectModal}
          onClose={() => setEInvoiceConnectModal(false)}
          invoiceGroupId={invoiceGroup.owner_id}
          refetchCallback={() => {
            refreshSenderStatus();
            refetchInvoiceGroup();
            setEInvoiceConnectEditModal(true);
          }}
        />
      )}
      {kivraConnectModal && (
        <KivraConnectModal
          kivraConnectModal={kivraConnectModal}
          setKivraConnectModal={setKivraConnectModal}
          invoiceGroupId={invoiceGroup.owner_id}
          refetchInvoiceGroup={refetchInvoiceGroup}
        />
      )}
      {kivraConnectEditModal && (
        <KivraConnectEditModal
          kivraConnectEditModal={kivraConnectEditModal}
          setKivraConnectEditModal={setKivraConnectEditModal}
          invoiceGroupId={invoiceGroup.owner_id}
        />
      )}
      {stripeConnectModal && (
        <StripeConnectModal
          stripeConnectModal={stripeConnectModal}
          setStripeConnectModal={setStripeConnectModal}
          invoiceGroupId={invoiceGroup.owner_id}
          stripeAccount={invoiceGroup.stripeAccount || undefined}
          refetchInvoiceGroup={refetchInvoiceGroup}
        />
      )}
    </>
  );
};
